import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { proXpApi, formatCents } from "@/lib/experienceApi";
import { parisWallToIso, formatParisDateShort, formatParisTime, formatParisDateTime, isoToParisDatetimeLocal, parisDatetimeLocalToIso } from "@/lib/dtParis";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  ArrowLeft, Ticket, Wine, Users, BarChart3, UserPlus,
  Rocket, Copy, Package, Share2, ExternalLink, Trash2, Sparkles,
  Send, HelpCircle, Plus, Search, ShoppingBag, Trophy, Play, Square, Crown, Medal, Pencil,
  Calendar,
} from "lucide-react";

export default function ProEventDetail() {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [tab, setTab] = useState("tickets");
  const nav = useNavigate();

  const load = () => proXpApi.getEvent(eventId).then(setEvent).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [eventId]);

  const publish = async () => {
    try { await proXpApi.publishEvent(eventId); toast.success("Événement publié !"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Impossible"); }
  };

  const [editing, setEditing] = useState(false);

  const deleteEvent = async () => {
    if (!window.confirm(`Supprimer définitivement "${event.name}" et toutes ses données ?`)) return;
    try {
      await proXpApi.deleteEvent(eventId);
      toast.success("Événement supprimé");
      nav(`/pro/experience/orgs/${event.organization_id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Impossible"); }
  };

  if (!event) return <div className="p-6 text-center text-sm text-neutral-500">Chargement…</div>;

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <Link to={`/pro/experience/orgs/${event.organization_id}`} className="text-sm text-neutral-600 inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="w-4 h-4" /> Organisation
      </Link>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display text-[#1F3A5F]">{event.name}</h1>
          <div className="text-xs text-neutral-500 mt-1">
            {formatParisDateTime(event.starts_at)} <span className="text-neutral-400">(Paris)</span>
            · <span className={
              event.status === "published" ? "text-emerald-600" :
              event.status === "live" ? "text-blue-600" : "text-neutral-500"
            }>{event.status}</span>
            · <span className="font-mono text-xs">ID: {event.id.slice(0, 8)}</span>
            <button onClick={() => { navigator.clipboard.writeText(event.id); toast.success("Copié"); }} className="ml-1">
              <Copy className="w-3 h-3 inline" />
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setEditing(!editing)} data-testid="edit-event">
            <Pencil className="w-4 h-4 mr-1" /> Modifier
          </Button>
          {event.status === "draft" && (
            <Button onClick={publish} data-testid="publish-event" className="bg-[#E5276A] hover:bg-[#C01661]">
              <Rocket className="w-4 h-4 mr-1" /> Publier
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={deleteEvent} data-testid="delete-event"
                  className="text-red-600 hover:bg-red-50">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {editing && <EditEventInfo event={event} onDone={() => { setEditing(false); load(); }} />}

      {event.status !== "draft" && <SharePanel event={event} />}
      <DemoTicketPanel eventId={eventId} />

      <div className="flex gap-1 mb-4 bg-neutral-100 rounded-xl p-1 overflow-x-auto">
        {[
          { k: "tickets", label: "Billets", icon: Ticket },
          { k: "slots", label: "Créneaux", icon: Calendar },
          { k: "wines", label: "Vins", icon: Wine },
          { k: "merch", label: "Boutique", icon: ShoppingBag },
          { k: "teams", label: "Équipes", icon: Users },
          { k: "staff", label: "Staff", icon: UserPlus },
          { k: "orders", label: "Commandes", icon: Package },
          { k: "palmares", label: "Palmarès", icon: Trophy },
          { k: "kpis", label: "KPIs", icon: BarChart3 },
        ].map((t) => {
          const Ic = t.icon;
          return (
            <button key={t.k} data-testid={`pro-tab-${t.k}`} onClick={() => setTab(t.k)}
              className={`px-3 py-2 rounded-lg text-xs font-medium flex items-center gap-1 whitespace-nowrap transition ${tab === t.k ? 'bg-white shadow text-[#E5276A]' : 'text-neutral-600'}`}>
              <Ic className="w-3.5 h-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "tickets" && <TicketsTab eventId={eventId} />}
      {tab === "slots" && <SlotsTab eventId={eventId} />}
      {tab === "wines" && <WinesTab eventId={eventId} />}
      {tab === "merch" && <MerchTab eventId={eventId} />}
      {tab === "teams" && <TeamsTab eventId={eventId} />}
      {tab === "staff" && <StaffTab eventId={eventId} />}
      {tab === "orders" && <OrdersTab eventId={eventId} />}
      {tab === "palmares" && <PalmaresTab eventId={eventId} />}
      {tab === "kpis" && <KpisTab eventId={eventId} />}
    </div>
  );
}

/* ────────── Demo Ticket Panel ────────── */
function DemoTicketPanel({ eventId }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("Démo");
  const [lastName, setLastName] = useState("Test");
  const [busy, setBusy] = useState(false);
  const [lastCode, setLastCode] = useState(null);

  const send = async () => {
    if (!email) { toast.error("Email requis"); return; }
    setBusy(true);
    try {
      const r = await proXpApi.createDemoTicket(eventId, {
        email: email.trim(),
        first_name: firstName.trim() || "Démo",
        last_name: lastName.trim() || "Test",
      });
      setLastCode(r.participant_code);
      toast.success(`Billet démo envoyé à ${email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible");
    } finally { setBusy(false); }
  };

  return (
    <Card className="my-4 border-dashed border-2 border-amber-300 bg-amber-50/40">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="text-xs uppercase tracking-widest text-amber-700 font-semibold mb-1 flex items-center gap-1">
              <Sparkles className="w-3.5 h-3.5" /> Compte démo (test sans paiement)
            </div>
            <p className="text-sm text-neutral-700">
              Envoyez un billet gratuit à votre email pour tester le parcours participant, l'email de confirmation et le QR.
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={() => setOpen(!open)} data-testid="toggle-demo">
            {open ? "Fermer" : "Générer un billet démo"}
          </Button>
        </div>
        {open && (
          <div className="mt-3 grid grid-cols-4 gap-2">
            <Input data-testid="demo-firstname" placeholder="Prénom" value={firstName}
                   onChange={(e) => setFirstName(e.target.value)} />
            <Input data-testid="demo-lastname" placeholder="NOM" value={lastName}
                   onChange={(e) => setLastName(e.target.value)} />
            <Input data-testid="demo-email" type="email" placeholder="votre@email.fr" value={email}
                   onChange={(e) => setEmail(e.target.value)} className="col-span-2" />
            <Button size="sm" onClick={send} disabled={busy} data-testid="send-demo"
                    className="col-span-4 bg-amber-500 hover:bg-amber-600 text-white">
              <Send className="w-3.5 h-3.5 mr-1" /> {busy ? "Envoi…" : "Envoyer le billet démo"}
            </Button>
            {lastCode && (
              <div className="col-span-4 text-xs bg-white rounded-md border px-3 py-2 flex items-center justify-between">
                <span>Code participant : <b className="font-mono text-[#E5276A]">{lastCode}</b></span>
                <button onClick={() => { navigator.clipboard.writeText(lastCode); toast.success("Copié"); }}>
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ────────── Tickets tab ────────── */

/* ────────── Edit event info (name, dates, description) ────────── */
function EditEventInfo({ event, onDone }) {
  const [f, setF] = useState({
    name: event.name || "",
    description: event.description || "",
    organizer_display_name: event.organizer_display_name || "",
    starts_at: isoToParisDatetimeLocal(event.starts_at),
    ends_at: isoToParisDatetimeLocal(event.ends_at),
    location_address: event.location?.address || "",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.name.trim() || !f.starts_at || !f.ends_at) {
      toast.error("Nom + dates requis"); return;
    }
    setBusy(true);
    try {
      await proXpApi.updateEvent(event.id, {
        name: f.name.trim(),
        description: f.description || null,
        organizer_display_name: f.organizer_display_name.trim() || null,
        starts_at: parisDatetimeLocalToIso(f.starts_at),
        ends_at: parisDatetimeLocalToIso(f.ends_at),
        location: f.location_address
          ? { ...(event.location || {}), address: f.location_address }
          : (event.location || null),
      });
      toast.success("Événement modifié");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible");
    } finally { setBusy(false); }
  };
  return (
    <Card className="mb-4 border-[#E5276A]/40" data-testid="edit-event-panel">
      <CardContent className="p-4 grid gap-2">
        <div className="text-xs uppercase tracking-widest text-[#E5276A] font-semibold">
          Modifier les informations de base
        </div>
        <div>
          <label className="text-xs text-neutral-600">Nom</label>
          <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}
                 data-testid="edit-name" />
        </div>
        <div>
          <label className="text-xs text-neutral-600">Nom de l'organisateur (affiché aux participants)</label>
          <Input value={f.organizer_display_name}
                 onChange={(e) => setF({ ...f, organizer_display_name: e.target.value })}
                 placeholder="Ex : Association œnologique de Sciences Po"
                 data-testid="edit-organizer" />
        </div>
        <div>
          <label className="text-xs text-neutral-600">Description</label>
          <textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })}
                    className="w-full border rounded-md px-3 py-2 text-sm min-h-[70px]"
                    data-testid="edit-description" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs text-neutral-600">Début <span className="text-[#E5276A]">(Paris)</span></label>
            <Input type="datetime-local" value={f.starts_at}
                   onChange={(e) => setF({ ...f, starts_at: e.target.value })}
                   data-testid="edit-starts" />
          </div>
          <div>
            <label className="text-xs text-neutral-600">Fin <span className="text-[#E5276A]">(Paris)</span></label>
            <Input type="datetime-local" value={f.ends_at}
                   onChange={(e) => setF({ ...f, ends_at: e.target.value })}
                   data-testid="edit-ends" />
          </div>
        </div>
        <div>
          <label className="text-xs text-neutral-600">Adresse</label>
          <Input value={f.location_address}
                 onChange={(e) => setF({ ...f, location_address: e.target.value })}
                 placeholder="Ex : 10 place de la République, Paris"
                 data-testid="edit-address" />
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="ghost" onClick={onDone}>Annuler</Button>
          <Button onClick={save} disabled={busy} data-testid="save-event"
                  className="bg-[#E5276A] hover:bg-[#C01661]">
            {busy ? "…" : "Enregistrer"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}


function TicketsTab({ eventId }) {
  const [tts, setTts] = useState([]);
  const [form, setForm] = useState({ name: "", price: 25, tastings: 5, description: "" });
  const load = () => proXpApi.listTicketTypes(eventId).then(setTts);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [eventId]);
  const create = async () => {
    if (!form.name.trim()) { toast.error("Nom requis"); return; }
    try {
      await proXpApi.createTicketType(eventId, {
        name: form.name, description: form.description || null,
        price_cents: Math.round(form.price * 100),
        tastings_included: form.tastings, active: true,
      });
      setForm({ name: "", price: 25, tastings: 5, description: "" }); load();
    } catch { toast.error("Impossible"); }
  };
  return (
    <div>
      <Card className="mb-3"><CardContent className="p-3 grid grid-cols-5 gap-2">
        <Input data-testid="tt-name" placeholder="Nom (ex. Niveau 1)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="col-span-2" />
        <Input placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <Input data-testid="tt-price" type="number" placeholder="€" value={form.price} onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })} />
        <div className="flex gap-1">
          <Input data-testid="tt-tastings" type="number" placeholder="Dég." value={form.tastings} onChange={(e) => setForm({ ...form, tastings: parseInt(e.target.value) || 1 })} />
          <Button data-testid="tt-create" onClick={create} className="bg-[#E5276A] hover:bg-[#C01661]">+</Button>
        </div>
      </CardContent></Card>
      <div className="grid gap-2">
        {tts.sort((a,b) => (a.order||0)-(b.order||0)).map(t => (
          <TicketTypeRow key={t.id} tt={t} eventId={eventId} onRefresh={load} />
        ))}
      </div>
    </div>
  );
}

function TicketTypeRow({ tt, eventId, onRefresh }) {
  const [edit, setEdit] = useState(false);
  const [f, setF] = useState({
    name: tt.name, description: tt.description || "",
    price: tt.price_cents / 100, tastings: tt.tastings_included, active: tt.active,
  });
  const save = async () => {
    try {
      await proXpApi.updateTicketType(eventId, tt.id, {
        name: f.name, description: f.description || null,
        price_cents: Math.round(f.price * 100),
        tastings_included: f.tastings, active: f.active,
      });
      toast.success("Enregistré"); setEdit(false); onRefresh();
    } catch { toast.error("Impossible"); }
  };
  const remove = async () => {
    if (!window.confirm(`Supprimer le billet "${tt.name}" ?`)) return;
    try { await proXpApi.deleteTicketType(eventId, tt.id); toast.success("Supprimé"); onRefresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Impossible"); }
  };
  if (!edit) return (
    <Card><CardContent className="p-3 flex items-center justify-between text-sm">
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-[#1F3A5F]">{tt.name}</span>
          {!tt.active && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-neutral-100 text-neutral-500">inactif</span>}
        </div>
        {tt.description && <div className="text-xs text-neutral-500">{tt.description}</div>}
        <div className="text-xs text-neutral-500">{tt.tastings_included} dégustations · vendus : {tt.sold || 0}</div>
      </div>
      <div className="flex items-center gap-2">
        <span className="font-bold text-[#E5276A]">{formatCents(tt.price_cents, tt.currency)}</span>
        <Button size="sm" variant="ghost" onClick={() => setEdit(true)}>✏</Button>
        <Button size="sm" variant="ghost" onClick={remove} className="text-red-500"><Trash2 className="w-3.5 h-3.5" /></Button>
      </div>
    </CardContent></Card>
  );
  return (
    <Card className="border-[#E5276A]"><CardContent className="p-3 grid grid-cols-5 gap-2">
      <Input value={f.name} onChange={(e) => setF({...f, name: e.target.value})} placeholder="Nom" className="col-span-2" />
      <Input value={f.description} onChange={(e) => setF({...f, description: e.target.value})} placeholder="Description" />
      <Input type="number" value={f.price} onChange={(e) => setF({...f, price: parseFloat(e.target.value) || 0})} placeholder="€" />
      <div className="flex gap-1">
        <Input type="number" value={f.tastings} onChange={(e) => setF({...f, tastings: parseInt(e.target.value) || 1})} placeholder="Dég." />
        <Button size="sm" onClick={save} className="bg-[#E5276A] hover:bg-[#C01661]">OK</Button>
        <Button size="sm" variant="ghost" onClick={() => setEdit(false)}>×</Button>
      </div>
      <label className="col-span-5 flex items-center gap-2 text-xs text-neutral-500">
        <input type="checkbox" checked={f.active} onChange={(e) => setF({...f, active: e.target.checked})} /> Actif
      </label>
    </CardContent></Card>
  );
}

/* ────────── Wines tab ────────── */
function WinesTab({ eventId }) {
  const [wines, setWines] = useState([]);
  const [ticketTypes, setTicketTypes] = useState([]);
  const [form, setForm] = useState({ name: "", slug: "", bottles: 10, doses: 8 });
  const load = () => {
    proXpApi.listWines(eventId).then(setWines);
    proXpApi.listTicketTypes(eventId).then(setTicketTypes).catch(() => setTicketTypes([]));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [eventId]);
  const create = async () => {
    if (!form.name.trim()) { toast.error("Nom requis"); return; }
    try {
      await proXpApi.createWine(eventId, {
        wine_ref_type: "appellation", wine_ref_slug: form.slug || null,
        wine_ref_name: form.name, initial_bottles: form.bottles, doses_per_bottle: form.doses,
      });
      setForm({ name: "", slug: "", bottles: 10, doses: 8 }); load();
    } catch { toast.error("Impossible"); }
  };
  return (
    <div>
      <Card className="mb-3"><CardContent className="p-3 grid grid-cols-5 gap-2">
        <Input placeholder="Nom du vin" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="col-span-2" />
        <Input placeholder="Slug appellation" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} />
        <Input type="number" placeholder="Bouteilles" value={form.bottles} onChange={(e) => setForm({ ...form, bottles: parseInt(e.target.value) || 0 })} />
        <Button onClick={create} className="bg-[#E5276A] hover:bg-[#C01661]">Ajouter</Button>
      </CardContent></Card>
      <div className="grid gap-2">
        {wines.map(w => (
          <WineRow key={w.id} wine={w} eventId={eventId} ticketTypes={ticketTypes} onRefresh={load} />
        ))}
      </div>
    </div>
  );
}

function WineRow({ wine: w, eventId, ticketTypes = [], onRefresh }) {
  const [editWine, setEditWine] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [savingTt, setSavingTt] = useState(false);
  const [f, setF] = useState({
    name: w.wine_ref?.name || "",
    bottle_enabled: !!w.bottle_purchase_enabled,
    bottle_price: (w.bottle_purchase_price_cents || 0) / 100,
    bottle_stock: w.bottle_purchase_stock || 0,
  });

  const allowed = w.ticket_types_allowed || [];
  const toggleTicketType = async (ttId) => {
    if (savingTt) return;
    const next = allowed.includes(ttId)
      ? allowed.filter((x) => x !== ttId)
      : [...allowed, ttId];
    try {
      setSavingTt(true);
      await proXpApi.updateWine(eventId, w.id, { ticket_types_allowed: next });
      onRefresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Impossible"); }
    finally { setSavingTt(false); }
  };
  const saveWine = async () => {
    try {
      await proXpApi.updateWine(eventId, w.id, {
        wine_ref_name: f.name,
        bottle_purchase_enabled: f.bottle_enabled,
        bottle_purchase_price_cents: Math.round(f.bottle_price * 100),
        bottle_purchase_stock: parseInt(f.bottle_stock) || 0,
      });
      toast.success("Enregistré"); setEditWine(false); onRefresh();
    } catch { toast.error("Impossible"); }
  };
  const remove = async () => {
    if (!window.confirm(`Supprimer le vin "${w.wine_ref?.name}" ?`)) return;
    try { await proXpApi.deleteWine(eventId, w.id); toast.success("Supprimé"); onRefresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Impossible"); }
  };
  const nbQuestions = (w.custom_questions || []).length;

  return (
    <Card><CardContent className="p-3 text-sm">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          {editWine ? (
            <div className="grid grid-cols-4 gap-2 mb-2">
              <Input value={f.name} onChange={(e) => setF({...f, name: e.target.value})} className="col-span-2" placeholder="Nom du vin" />
              <label className="flex items-center gap-1 text-xs col-span-2">
                <input type="checkbox" checked={f.bottle_enabled} onChange={(e) => setF({...f, bottle_enabled: e.target.checked})} />
                Vendre la bouteille
              </label>
              {f.bottle_enabled && (<>
                <Input type="number" step="0.5" value={f.bottle_price} onChange={(e) => setF({...f, bottle_price: parseFloat(e.target.value)||0})} placeholder="Prix bouteille €" />
                <Input type="number" value={f.bottle_stock} onChange={(e) => setF({...f, bottle_stock: e.target.value})} placeholder="Stock" />
              </>)}
              <div className="col-span-4 flex justify-end gap-1">
                <Button size="sm" onClick={saveWine} className="bg-[#E5276A] hover:bg-[#C01661]">OK</Button>
                <Button size="sm" variant="ghost" onClick={() => setEditWine(false)}>×</Button>
              </div>
            </div>
          ) : (
            <div>
              <div className="font-semibold text-[#1F3A5F] flex items-center gap-2">
                {w.wine_ref?.name}
                <Button size="sm" variant="ghost" className="h-5 px-1" onClick={() => setEditWine(true)}>✏</Button>
                <Button size="sm" variant="ghost" className="h-5 px-1 text-red-500" onClick={remove}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
              <div className="text-xs text-neutral-500">
                {w.inventory?.doses_remaining} / {w.inventory?.initial_doses} doses · {w.inventory?.doses_served} servies
                {w.bottle_purchase_enabled && (
                  <span className="ml-2 text-[#E5276A] font-semibold">
                    Bouteille : {(w.bottle_purchase_price_cents/100).toFixed(0)}€ · stock {w.bottle_purchase_stock}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
        <span className={`text-xs px-2 py-1 rounded-full ml-2 ${w.inventory?.status === "sold_out" ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>
          {w.inventory?.status === "sold_out" ? "Épuisé" : "Dispo"}
        </span>
      </div>
      <div className="mt-2 border-t pt-2">
        <div className="text-[11px] text-neutral-500 mb-1 flex items-center gap-1">
          <Ticket className="w-3 h-3" />
          Billets autorisés
          {allowed.length === 0 && (
            <span className="ml-1 italic text-neutral-400">— aucun filtre (visible par tous)</span>
          )}
        </div>
        {ticketTypes.length === 0 ? (
          <p className="text-[11px] text-neutral-400 italic">
            Créez d'abord des types de billets dans l'onglet Billets.
          </p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {ticketTypes.map((tt) => {
              const on = allowed.includes(tt.id);
              return (
                <button
                  key={tt.id}
                  type="button"
                  disabled={savingTt}
                  onClick={() => toggleTicketType(tt.id)}
                  data-testid={`wine-tt-chip-${w.id}-${tt.id}`}
                  className={`text-[11px] px-2 py-0.5 rounded-full border transition ${
                    on
                      ? "bg-[#E5276A] text-white border-[#E5276A]"
                      : "bg-neutral-50 text-neutral-600 border-neutral-200 hover:border-[#E5276A] hover:text-[#E5276A]"
                  } ${savingTt ? "opacity-50 cursor-wait" : ""}`}
                >
                  {tt.name}
                </button>
              );
            })}
          </div>
        )}
      </div>
      <div className="mt-2">
        <button onClick={() => setShowQuiz(!showQuiz)}
                className="flex items-center gap-1 text-xs text-[#E5276A] font-medium hover:underline">
          <HelpCircle className="w-3.5 h-3.5" />
          Quiz pédagogique · {nbQuestions > 0 ? `${nbQuestions} question${nbQuestions>1?'s':''} perso` : w.quiz_id ? `cours "${w.quiz_id}"` : "non configuré"}
          <span className="ml-1 text-neutral-400">{showQuiz ? "▲" : "▼"}</span>
        </button>
        {showQuiz && <QuizEditor wine={w} eventId={eventId} onRefresh={onRefresh} />}
      </div>
    </CardContent></Card>
  );
}

/* ────────── Quiz editor (custom questions + academy picker + KMS bricks) ────────── */
function QuizEditor({ wine, eventId, onRefresh }) {
  const [tab, setTab] = useState(wine.custom_questions?.length ? "custom" : "academy");
  return (
    <div className="mt-2 border-t pt-3">
      <div className="flex gap-2 mb-3 text-xs flex-wrap">
        <button data-testid="qtab-academy" onClick={() => setTab("academy")}
                className={`px-2 py-1 rounded ${tab === "academy" ? "bg-[#E5276A] text-white" : "bg-neutral-100"}`}>
          Cours Academy
        </button>
        <button data-testid="qtab-custom" onClick={() => setTab("custom")}
                className={`px-2 py-1 rounded ${tab === "custom" ? "bg-[#E5276A] text-white" : "bg-neutral-100"}`}>
          Questions perso
        </button>
        <button data-testid="qtab-kms" onClick={() => setTab("kms")}
                className={`px-2 py-1 rounded ${tab === "kms" ? "bg-[#E5276A] text-white" : "bg-neutral-100"}`}>
          Contenu KMS ({(wine.content_block_ids || []).length})
        </button>
      </div>
      {tab === "academy" && <AcademyPicker wine={wine} eventId={eventId} onRefresh={onRefresh} />}
      {tab === "custom" && <CustomQuestionsEditor wine={wine} eventId={eventId} onRefresh={onRefresh} />}
      {tab === "kms" && <KmsBlocksPicker wine={wine} eventId={eventId} onRefresh={onRefresh} />}
    </div>
  );
}

function AcademyPicker({ wine, eventId, onRefresh }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(wine.quiz_id || "");
  const [manualSlug, setManualSlug] = useState(wine.quiz_id || "");
  const search = async () => {
    try {
      const r = await proXpApi.searchAcademyCourses(eventId, q);
      setItems(r);
    } catch { toast.error("Recherche impossible"); }
  };
  useEffect(() => { search(); /* eslint-disable-next-line */ }, []);
  const link = async (slug) => {
    try {
      await proXpApi.updateWine(eventId, wine.id, { quiz_id: slug || null });
      setSelected(slug); toast.success(slug ? "Cours lié" : "Cours retiré"); onRefresh();
    } catch { toast.error("Impossible"); }
  };
  return (
    <div>
      <div className="flex gap-1 mb-2">
        <div className="flex-1 flex items-center gap-1 border rounded px-2 py-1">
          <Search className="w-3.5 h-3.5 text-neutral-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && search()}
                 placeholder="Rechercher un cours…"
                 className="flex-1 text-xs outline-none" data-testid="academy-search-input" />
        </div>
        <Button size="sm" onClick={search} className="bg-[#1F3A5F] text-white text-xs h-8">Chercher</Button>
      </div>
      <div className="grid gap-1 max-h-56 overflow-y-auto mb-2">
        {items.map(c => (
          <div key={c.slug}
               className={`p-2 rounded border text-xs flex items-center justify-between ${selected === c.slug ? "border-[#E5276A] bg-[#E5276A]/5" : ""}`}>
            <div>
              <div className="font-semibold">{c.title}</div>
              <div className="font-mono text-[10px] text-neutral-500">{c.slug}</div>
            </div>
            <Button size="sm" variant={selected === c.slug ? "default" : "outline"}
                    className={selected === c.slug ? "bg-[#E5276A] hover:bg-[#C01661] text-xs h-7" : "text-xs h-7"}
                    onClick={() => link(c.slug)} data-testid={`link-${c.slug}`}>
              {selected === c.slug ? "Lié ✓" : "Lier"}
            </Button>
          </div>
        ))}
        {items.length === 0 && <p className="text-xs text-neutral-500 text-center py-3">Aucun cours trouvé</p>}
      </div>
      <div className="border-t pt-2 flex items-center gap-1">
        <span className="text-[10px] text-neutral-500">Slug manuel :</span>
        <input value={manualSlug} onChange={(e) => setManualSlug(e.target.value)}
               placeholder="slug-du-cours" className="border rounded px-2 py-0.5 text-xs flex-1 font-mono"
               data-testid="manual-slug-input" />
        <Button size="sm" onClick={() => link(manualSlug || null)}
                className="bg-[#E5276A] hover:bg-[#C01661] h-6 px-2 text-xs">OK</Button>
        {selected && (
          <Button size="sm" variant="ghost" onClick={() => link(null)} className="h-6 px-2 text-xs text-red-500">
            Retirer
          </Button>
        )}
      </div>
    </div>
  );
}


function KmsBlocksPicker({ wine, eventId, onRefresh }) {
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(wine.content_block_ids || []);
  const [saving, setSaving] = useState(false);

  const search = async () => {
    try {
      const r = await proXpApi.searchAtlasBlocks(eventId, q, category);
      setItems(r);
    } catch { toast.error("Recherche impossible"); }
  };
  useEffect(() => { search(); /* eslint-disable-next-line */ }, []);

  const toggle = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const save = async () => {
    setSaving(true);
    try {
      await proXpApi.updateWine(eventId, wine.id, { content_block_ids: selected });
      toast.success(`${selected.length} brique${selected.length>1?"s":""} liée${selected.length>1?"s":""}`);
      onRefresh();
    } catch { toast.error("Impossible"); }
    finally { setSaving(false); }
  };

  const CATS = [
    ["", "Toutes"], ["histoire", "📜 Histoire"], ["terroir_geologie", "🪨 Terroir"],
    ["cepages", "🍇 Cépages"], ["vinification", "🍷 Vinification"],
    ["accords", "🍽️ Accords"], ["anecdotes", "🎉 Anecdotes"],
    ["saviez_vous", "💡 Saviez-vous"], ["chiffres_cles", "📊 Chiffres"],
  ];

  return (
    <div>
      <p className="text-xs text-neutral-500 mb-2">
        Piochez dans les briques du KMS pour construire une fiche pédagogique de dégustation. L'ordre choisi ici sera respecté.
      </p>
      <div className="flex gap-1 mb-2">
        <select value={category} onChange={(e) => setCategory(e.target.value)}
                className="border rounded px-2 py-1 text-xs bg-white">
          {CATS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <input value={q} onChange={(e) => setQ(e.target.value)}
               onKeyDown={(e) => e.key === "Enter" && search()}
               placeholder="Rechercher…"
               className="flex-1 border rounded px-2 py-1 text-xs"
               data-testid="kms-search-input" />
        <Button size="sm" onClick={search} className="bg-[#1F3A5F] text-white h-7 text-xs">Chercher</Button>
      </div>
      <div className="grid gap-1 max-h-64 overflow-y-auto mb-2">
        {items.length === 0 && <p className="text-xs text-neutral-500 text-center py-3">Aucune brique trouvée</p>}
        {items.map(b => (
          <div key={b.id} onClick={() => toggle(b.id)}
               className={`p-2 rounded border text-xs cursor-pointer transition ${selected.includes(b.id) ? "border-[#E5276A] bg-[#E5276A]/5" : "hover:border-neutral-300"}`}>
            <div className="flex items-start gap-2">
              <input type="checkbox" checked={selected.includes(b.id)} readOnly className="mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[#1F3A5F] truncate">{b.title}</div>
                <div className="text-[10px] text-neutral-400">{b.category}{b.media_count ? ` · ${b.media_count} médias` : ""}</div>
                <div className="text-neutral-500 line-clamp-2">{b.preview}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between">
        <span className="text-xs text-neutral-500">{selected.length} sélectionnée{selected.length>1?"s":""}</span>
        <Button size="sm" onClick={save} disabled={saving} data-testid="save-kms"
                className="bg-[#E5276A] hover:bg-[#C01661]">
          {saving ? "…" : "Enregistrer"}
        </Button>
      </div>
    </div>
  );
}

function CustomQuestionsEditor({ wine, eventId, onRefresh }) {
  const [questions, setQuestions] = useState(wine.custom_questions || []);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setQuestions(wine.custom_questions || []); }, [wine.id]);

  const add = () => setQuestions([
    ...questions,
    { id: `c${Date.now()}`, kind: "qcm", question: "", choices: ["", "", "", ""], correct: "A" },
  ]);
  const update = (i, patch) => {
    const copy = [...questions]; copy[i] = { ...copy[i], ...patch }; setQuestions(copy);
  };
  const updateChoice = (i, ci, val) => {
    const copy = [...questions]; const choices = [...(copy[i].choices || [])];
    choices[ci] = val; copy[i] = { ...copy[i], choices }; setQuestions(copy);
  };
  const remove = (i) => setQuestions(questions.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      // Clean up empty questions and validate
      const clean = questions
        .filter(q => q.question?.trim())
        .map(q => ({
          id: q.id, kind: "qcm",
          question: q.question.trim(),
          choices: (q.choices || []).map(c => c.trim()).filter(Boolean),
          correct: (q.correct || "A").toUpperCase(),
        }));
      await proXpApi.updateWine(eventId, wine.id, { custom_questions: clean });
      toast.success(`${clean.length} question${clean.length>1?"s":""} enregistrée${clean.length>1?"s":""}`);
      onRefresh();
    } catch { toast.error("Impossible"); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <p className="text-xs text-neutral-500 mb-2">
        Créez vos propres questions (QCM). Si des questions perso sont présentes, elles remplacent le cours Academy pour ce vin.
      </p>
      <div className="space-y-2 mb-2 max-h-96 overflow-y-auto">
        {questions.map((q, i) => (
          <div key={q.id} className="border rounded p-2 bg-white">
            <div className="flex items-start gap-1 mb-2">
              <span className="text-xs font-semibold text-neutral-500 pt-1">Q{i+1}</span>
              <textarea value={q.question} onChange={(e) => update(i, { question: e.target.value })}
                        placeholder="Énoncé de la question…"
                        className="flex-1 border rounded px-2 py-1 text-xs min-h-[40px]"
                        data-testid={`q-${i}-text`} />
              <Button size="sm" variant="ghost" onClick={() => remove(i)} className="text-red-500 h-6 px-1">
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-1 pl-6">
              {(q.choices || ["", "", "", ""]).map((c, ci) => {
                const letter = String.fromCharCode(65 + ci); // A B C D
                const isCorrect = (q.correct || "").toUpperCase() === letter;
                return (
                  <label key={ci} className={`flex items-center gap-1 border rounded px-2 py-1 text-xs cursor-pointer ${isCorrect ? "border-emerald-500 bg-emerald-50" : ""}`}>
                    <input type="radio" checked={isCorrect}
                           onChange={() => update(i, { correct: letter })}
                           name={`correct-${q.id}`} />
                    <span className="font-mono">{letter}.</span>
                    <input value={c} onChange={(e) => updateChoice(i, ci, e.target.value)}
                           placeholder={`Choix ${letter}`}
                           className="flex-1 outline-none bg-transparent"
                           data-testid={`q-${i}-choice-${ci}`} />
                  </label>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <div className="flex justify-between items-center">
        <Button size="sm" variant="outline" onClick={add} data-testid="add-question">
          <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter une question
        </Button>
        <Button size="sm" onClick={save} disabled={saving} data-testid="save-questions"
                className="bg-[#E5276A] hover:bg-[#C01661]">
          {saving ? "…" : "Enregistrer"}
        </Button>
      </div>
    </div>
  );
}

/* ────────── Slots tab ────────── */
function SlotsTab({ eventId }) {
  const [slots, setSlots] = useState([]);
  const [form, setForm] = useState({ date: "", start: "", end: "", capacity: 20 });
  const [bulk, setBulk] = useState({ date: "", start: "10:00", end: "18:00", step: 60, capacity: 20 });
  const [busy, setBusy] = useState(false);
  const [shiftHours, setShiftHours] = useState(0);

  const load = () => proXpApi.listSlots(eventId).then(setSlots);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [eventId]);

  // All slot times are anchored to Europe/Paris regardless of the organizer's
  // browser timezone. `parisWallToIso` produces the correct UTC ISO from a
  // wall-clock date+time meant to be Paris time.
  const toIso = (date, time) => parisWallToIso(date, time);

  const createOne = async () => {
    const s = toIso(form.date, form.start);
    const e = toIso(form.date, form.end);
    if (!s || !e) { toast.error("Date, heure début et heure fin requises"); return; }
    if (new Date(e) <= new Date(s)) { toast.error("L'heure de fin doit être après le début"); return; }
    const cap = parseInt(form.capacity);
    if (!cap || cap < 1) { toast.error("Capacité invalide"); return; }
    try {
      setBusy(true);
      await proXpApi.createSlot(eventId, { starts_at: s, ends_at: e, capacity: cap });
      toast.success("Créneau ajouté");
      setForm({ date: "", start: "", end: "", capacity: 20 });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Impossible"); }
    finally { setBusy(false); }
  };

  const createBulk = async () => {
    if (!bulk.date || !bulk.start || !bulk.end) { toast.error("Date, début et fin requis"); return; }
    const step = parseInt(bulk.step); const cap = parseInt(bulk.capacity);
    if (!step || step < 5) { toast.error("Durée invalide (min. 5 min)"); return; }
    if (!cap || cap < 1) { toast.error("Capacité invalide"); return; }
    const startIso = toIso(bulk.date, bulk.start);
    const endIso = toIso(bulk.date, bulk.end);
    if (!startIso || !endIso) { toast.error("Date/heures invalides"); return; }
    const startD = new Date(startIso);
    const endD = new Date(endIso);
    if (endD <= startD) { toast.error("Fin doit être après début"); return; }
    const slotsToCreate = [];
    let cur = new Date(startD);
    while (cur.getTime() + step * 60000 <= endD.getTime()) {
      const next = new Date(cur.getTime() + step * 60000);
      slotsToCreate.push({ starts_at: cur.toISOString(), ends_at: next.toISOString(), capacity: cap });
      cur = next;
    }
    if (!slotsToCreate.length) { toast.error("Aucun créneau à créer avec ces bornes"); return; }
    if (!window.confirm(`Créer ${slotsToCreate.length} créneaux de ${step} min ?`)) return;
    try {
      setBusy(true);
      for (const s of slotsToCreate) {
        await proXpApi.createSlot(eventId, s);
      }
      toast.success(`${slotsToCreate.length} créneaux créés`);
      setBulk({ date: "", start: "10:00", end: "18:00", step: 60, capacity: 20 });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur lors de la génération"); }
    finally { setBusy(false); }
  };

  const shiftAll = async () => {
    const h = parseFloat(shiftHours);
    if (!h || Number.isNaN(h)) { toast.error("Décalage invalide"); return; }
    if (!window.confirm(
      `Décaler TOUS les créneaux de ${h > 0 ? "+" : ""}${h}h ?\n\n` +
      `Utile pour corriger des créneaux créés dans un mauvais fuseau. Les billets déjà émis conservent leur créneau — seule leur heure affichée changera.`
    )) return;
    try {
      setBusy(true);
      const deltaMs = h * 3600 * 1000;
      for (const s of slots) {
        const newStart = new Date(new Date(s.starts_at).getTime() + deltaMs).toISOString();
        const newEnd = new Date(new Date(s.ends_at).getTime() + deltaMs).toISOString();
        await proXpApi.updateSlot(eventId, s.id, { starts_at: newStart, ends_at: newEnd });
      }
      toast.success(`${slots.length} créneaux décalés`);
      setShiftHours(0);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Impossible"); }
    finally { setBusy(false); }
  };

  const remove = async (s) => {
    if (!window.confirm("Supprimer ce créneau ?")) return;
    try { await proXpApi.deleteSlot(eventId, s.id); toast.success("Supprimé"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Impossible"); }
  };

  const updateCapacity = async (s, newCap) => {
    const n = parseInt(newCap);
    if (!n || n < 1) return;
    try { await proXpApi.updateSlot(eventId, s.id, { capacity: n }); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Impossible"); }
  };

  const toggleStatus = async (s) => {
    const next = s.status === "closed" ? "open" : "closed";
    try {
      await proXpApi.updateSlot(eventId, s.id, { status: next });
      toast.success(next === "closed" ? "Créneau fermé" : "Créneau ouvert");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Impossible"); }
  };

  return (
    <div className="space-y-3">
      <Card><CardContent className="p-3 space-y-3">
        <div>
          <p className="text-sm font-semibold text-[#1F3A5F]">Ajouter un créneau</p>
          <p className="text-xs text-neutral-500 mt-0.5">
            Les participants choisiront leur créneau au moment du checkout.{" "}
            <span className="text-[#E5276A] font-medium">Toutes les heures sont en Europe/Paris</span>, quel que soit votre fuseau.
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          <div className="col-span-2 sm:col-span-1">
            <label className="text-[11px] text-neutral-500">Date</label>
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                   data-testid="slot-date" />
          </div>
          <div>
            <label className="text-[11px] text-neutral-500">Début</label>
            <Input type="time" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })}
                   data-testid="slot-start" />
          </div>
          <div>
            <label className="text-[11px] text-neutral-500">Fin</label>
            <Input type="time" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })}
                   data-testid="slot-end" />
          </div>
          <div>
            <label className="text-[11px] text-neutral-500">Capacité</label>
            <Input type="number" min="1" value={form.capacity}
                   onChange={(e) => setForm({ ...form, capacity: e.target.value })}
                   data-testid="slot-capacity" />
          </div>
          <div className="flex items-end">
            <Button onClick={createOne} disabled={busy} data-testid="slot-create"
                    className="bg-[#E5276A] hover:bg-[#C01661] w-full">
              <Plus className="w-4 h-4 mr-1" /> Ajouter
            </Button>
          </div>
        </div>
      </CardContent></Card>

      <Card><CardContent className="p-3 space-y-3">
        <div>
          <p className="text-sm font-semibold text-[#1F3A5F]">Génération en série</p>
          <p className="text-xs text-neutral-500 mt-0.5">
            Générer plusieurs créneaux d'affilée sur une plage horaire.
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-6 gap-2">
          <div className="col-span-2 sm:col-span-1">
            <label className="text-[11px] text-neutral-500">Date</label>
            <Input type="date" value={bulk.date} onChange={(e) => setBulk({ ...bulk, date: e.target.value })}
                   data-testid="bulk-date" />
          </div>
          <div>
            <label className="text-[11px] text-neutral-500">Début</label>
            <Input type="time" value={bulk.start} onChange={(e) => setBulk({ ...bulk, start: e.target.value })} />
          </div>
          <div>
            <label className="text-[11px] text-neutral-500">Fin</label>
            <Input type="time" value={bulk.end} onChange={(e) => setBulk({ ...bulk, end: e.target.value })} />
          </div>
          <div>
            <label className="text-[11px] text-neutral-500">Durée (min)</label>
            <Input type="number" min="5" value={bulk.step}
                   onChange={(e) => setBulk({ ...bulk, step: e.target.value })} />
          </div>
          <div>
            <label className="text-[11px] text-neutral-500">Capacité</label>
            <Input type="number" min="1" value={bulk.capacity}
                   onChange={(e) => setBulk({ ...bulk, capacity: e.target.value })} />
          </div>
          <div className="flex items-end">
            <Button onClick={createBulk} disabled={busy} data-testid="bulk-create"
                    variant="outline" className="w-full">
              <Sparkles className="w-4 h-4 mr-1" /> Générer
            </Button>
          </div>
        </div>
      </CardContent></Card>

      <div className="grid gap-2">
        {slots.length > 0 && (
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-3">
              <div className="text-xs font-semibold text-amber-900 mb-1">
                Décaler tous les créneaux (correctif fuseau horaire)
              </div>
              <div className="text-[11px] text-amber-800 mb-2">
                Vos créneaux affichent une heure décalée ? Appliquez un décalage global (ex&nbsp;: −1h si les slots étaient créés en heure UK au lieu de Paris). Les billets déjà émis conservent leur créneau — seule l'heure affichée changera.
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Input type="number" step="0.5" value={shiftHours}
                       onChange={(e) => setShiftHours(e.target.value)}
                       className="w-24 h-8 text-sm"
                       data-testid="slot-shift-hours"
                       placeholder="±h" />
                <span className="text-xs text-amber-800">heures</span>
                <Button size="sm" variant="outline" onClick={shiftAll} disabled={busy}
                        data-testid="slot-shift-apply"
                        className="border-amber-500 text-amber-900 hover:bg-amber-100">
                  Appliquer à {slots.length} créneau{slots.length > 1 ? "x" : ""}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        {slots.length === 0 && (
          <p className="text-sm text-neutral-500 text-center py-6">
            Aucun créneau. Ajoutez-en pour permettre aux participants de réserver leur horaire.
          </p>
        )}
        {slots.map((s) => {
          const dateStr = formatParisDateShort(s.starts_at);
          const hStart = formatParisTime(s.starts_at);
          const hEnd = formatParisTime(s.ends_at);
          const sold = s.tickets_sold || 0;
          const full = sold >= (s.capacity || 0);
          const closed = s.status === "closed";
          return (
            <Card key={s.id} data-testid={`slot-row-${s.id}`}>
              <CardContent className="p-3 flex flex-wrap items-center gap-3 justify-between">
                <div className="min-w-0">
                  <div className="font-semibold text-[#1F3A5F] text-sm">
                    {dateStr} · {hStart} → {hEnd}
                  </div>
                  <div className="text-xs text-neutral-500 mt-0.5 flex flex-wrap items-center gap-2">
                    <span>{sold} / {s.capacity} vendus</span>
                    {full && <span className="px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 text-[10px] font-medium">Complet</span>}
                    {closed && <span className="px-1.5 py-0.5 rounded bg-neutral-200 text-neutral-700 text-[10px] font-medium">Fermé</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-[11px] text-neutral-500">Capacité</div>
                  <Input type="number" min={sold || 1} defaultValue={s.capacity}
                         onBlur={(e) => {
                           const v = parseInt(e.target.value);
                           if (v && v !== s.capacity) updateCapacity(s, v);
                         }}
                         className="w-20 h-8 text-sm" />
                  <Button size="sm" variant="outline" onClick={() => toggleStatus(s)}
                          data-testid={`slot-toggle-${s.id}`}>
                    {closed ? "Rouvrir" : "Fermer"}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(s)}
                          disabled={sold > 0} title={sold > 0 ? "Des billets sont rattachés" : ""}
                          className="text-red-500" data-testid={`slot-delete-${s.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}


/* ────────── Teams tab ────────── */
function TeamsTab({ eventId }) {
  const [teams, setTeams] = useState([]);
  const [form, setForm] = useState({ name: "", capacity: "" });
  const load = () => proXpApi.listEventTeams(eventId).then(setTeams);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [eventId]);
  const create = async () => {
    if (!form.name.trim()) { toast.error("Nom requis"); return; }
    try {
      await proXpApi.createEventTeam(eventId, {
        name: form.name.trim(),
        capacity: form.capacity ? parseInt(form.capacity) : null,
      });
      setForm({ name: "", capacity: "" }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Impossible"); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Supprimer l'équipe "${t.name}" ?`)) return;
    try { await proXpApi.deleteEventTeam(eventId, t.id); toast.success("Supprimée"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Impossible"); }
  };
  return (
    <div>
      <Card className="mb-3"><CardContent className="p-3">
        <p className="text-xs text-neutral-500 mb-2">
          Les équipes que vous créez ici apparaîtront en liste déroulante au checkout : chaque participant choisit son équipe au démarrage.
        </p>
        <div className="grid grid-cols-4 gap-2">
          <Input placeholder="Nom de l'équipe" value={form.name}
                 onChange={(e) => setForm({ ...form, name: e.target.value })}
                 className="col-span-2" data-testid="team-name" />
          <Input placeholder="Capacité (opt.)" value={form.capacity}
                 onChange={(e) => setForm({ ...form, capacity: e.target.value })} />
          <Button onClick={create} className="bg-[#E5276A] hover:bg-[#C01661]" data-testid="team-create">
            <Plus className="w-4 h-4 mr-1" /> Ajouter
          </Button>
        </div>
      </CardContent></Card>
      <div className="grid gap-2">
        {teams.length === 0 && <p className="text-sm text-neutral-500 text-center py-6">Aucune équipe.</p>}
        {teams.map(t => (
          <Card key={t.id}><CardContent className="p-3 flex items-center justify-between text-sm">
            <div>
              <div className="font-semibold text-[#1F3A5F]">{t.name}</div>
              <div className="text-xs text-neutral-500">
                {t.member_count} membre{t.member_count > 1 ? "s" : ""}
                {t.capacity ? ` · capacité ${t.capacity}` : ""}
                · {t.total_points_cache || 0} pts
              </div>
            </div>
            <Button size="sm" variant="ghost" onClick={() => remove(t)} className="text-red-500">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </CardContent></Card>
        ))}
      </div>
    </div>
  );
}

/* ────────── Staff / Orders / KPIs (unchanged) ────────── */

/* ────────── Merch tab (organizer catalog) ────────── */
function MerchTab({ eventId }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", description: "", image_url: "", price: 15, stock: 20, kind: "merch" });
  const load = () => proXpApi.listMerch(eventId).then(setItems);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [eventId]);
  const create = async () => {
    if (!form.name.trim()) { toast.error("Nom requis"); return; }
    try {
      await proXpApi.createMerch(eventId, {
        name: form.name.trim(),
        description: form.description || null,
        image_url: form.image_url || null,
        price_cents: Math.round(parseFloat(form.price) * 100),
        stock: parseInt(form.stock) || 0,
        kind: form.kind, active: true,
      });
      setForm({ name: "", description: "", image_url: "", price: 15, stock: 20, kind: "merch" });
      load();
    } catch { toast.error("Impossible"); }
  };
  return (
    <div>
      <Card className="mb-3"><CardContent className="p-3 grid grid-cols-6 gap-2">
        <Input placeholder="Nom (T-shirt…)" value={form.name}
               onChange={(e) => setForm({ ...form, name: e.target.value })}
               className="col-span-2" data-testid="merch-name" />
        <Input placeholder="Description" value={form.description}
               onChange={(e) => setForm({ ...form, description: e.target.value })}
               className="col-span-2" />
        <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}
                className="border rounded-md px-2 py-1 text-sm bg-white">
          <option value="merch">Objet</option>
          <option value="gift_box">Coffret</option>
        </select>
        <Input placeholder="Image URL (opt.)" value={form.image_url}
               onChange={(e) => setForm({ ...form, image_url: e.target.value })}
               className="col-span-3" />
        <Input type="number" step="0.5" placeholder="Prix €" value={form.price}
               onChange={(e) => setForm({ ...form, price: e.target.value })} />
        <Input type="number" placeholder="Stock" value={form.stock}
               onChange={(e) => setForm({ ...form, stock: e.target.value })} />
        <Button onClick={create} className="bg-[#E5276A] hover:bg-[#C01661]" data-testid="merch-create">
          <Plus className="w-4 h-4" />
        </Button>
      </CardContent></Card>
      <div className="grid gap-2">
        {items.length === 0 && <p className="text-sm text-neutral-500 text-center py-6">
          Aucun article. Ajoutez t-shirts, verres, coffrets — les participants pourront les acheter après leur dégustation.
        </p>}
        {items.map(m => (
          <MerchRow key={m.id} item={m} eventId={eventId} onRefresh={load} />
        ))}
      </div>
    </div>
  );
}

function MerchRow({ item: m, eventId, onRefresh }) {
  const [edit, setEdit] = useState(false);
  const [f, setF] = useState({
    name: m.name, description: m.description || "", image_url: m.image_url || "",
    price: m.price_cents / 100, stock: m.stock, active: m.active,
  });
  const save = async () => {
    try {
      await proXpApi.updateMerch(eventId, m.id, {
        name: f.name, description: f.description || null,
        image_url: f.image_url || null,
        price_cents: Math.round(parseFloat(f.price) * 100),
        stock: parseInt(f.stock) || 0, active: f.active,
      });
      toast.success("Enregistré"); setEdit(false); onRefresh();
    } catch { toast.error("Impossible"); }
  };
  const remove = async () => {
    if (!window.confirm(`Supprimer "${m.name}" ?`)) return;
    try { await proXpApi.deleteMerch(eventId, m.id); toast.success("Supprimé"); onRefresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Impossible"); }
  };
  if (!edit) return (
    <Card><CardContent className="p-3 flex items-center gap-3">
      {m.image_url ? (
        <img src={m.image_url} alt={m.name} className="w-14 h-14 rounded-md object-cover bg-neutral-100" />
      ) : (
        <div className="w-14 h-14 rounded-md bg-[#E5276A]/10 flex items-center justify-center">
          <ShoppingBag className="w-6 h-6 text-[#E5276A]" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm truncate flex items-center gap-2">
          {m.name}
          {!m.active && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-neutral-100 text-neutral-500">inactif</span>}
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-neutral-100 text-neutral-500">{m.kind === "gift_box" ? "coffret" : "objet"}</span>
        </div>
        {m.description && <div className="text-xs text-neutral-500 truncate">{m.description}</div>}
        <div className="text-xs text-neutral-500">stock {m.stock} · vendus {m.sold || 0}</div>
      </div>
      <div className="flex items-center gap-2">
        <span className="font-bold text-[#E5276A]">{(m.price_cents/100).toFixed(2)}€</span>
        <Button size="sm" variant="ghost" onClick={() => setEdit(true)}>✏</Button>
        <Button size="sm" variant="ghost" onClick={remove} className="text-red-500">
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </CardContent></Card>
  );
  return (
    <Card className="border-[#E5276A]"><CardContent className="p-3 grid grid-cols-6 gap-2">
      <Input value={f.name} onChange={(e) => setF({...f, name: e.target.value})} className="col-span-2" placeholder="Nom" />
      <Input value={f.description} onChange={(e) => setF({...f, description: e.target.value})} className="col-span-2" placeholder="Description" />
      <Input type="number" step="0.5" value={f.price} onChange={(e) => setF({...f, price: e.target.value})} placeholder="€" />
      <Input type="number" value={f.stock} onChange={(e) => setF({...f, stock: e.target.value})} placeholder="Stock" />
      <Input value={f.image_url} onChange={(e) => setF({...f, image_url: e.target.value})} className="col-span-4" placeholder="Image URL" />
      <label className="col-span-2 flex items-center gap-1 text-xs">
        <input type="checkbox" checked={f.active} onChange={(e) => setF({...f, active: e.target.checked})} /> Actif
      </label>
      <Button size="sm" onClick={save} className="bg-[#E5276A] hover:bg-[#C01661] col-span-5">OK</Button>
      <Button size="sm" variant="ghost" onClick={() => setEdit(false)}>×</Button>
    </CardContent></Card>
  );
}


function StaffTab({ eventId }) {
  const [staff, setStaff] = useState([]);
  const [form, setForm] = useState({ email: "", name: "" });
  const load = () => proXpApi.listStaff(eventId).then(setStaff);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [eventId]);
  const invite = async () => {
    try {
      const s = await proXpApi.inviteStaff(eventId, { email: form.email, name: form.name });
      toast.success(`PIN: ${s.pin}`);
      setForm({ email: "", name: "" }); load();
    } catch { toast.error("Impossible"); }
  };
  return (
    <div>
      <Card className="mb-3"><CardContent className="p-3 grid grid-cols-3 gap-2">
        <Input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <Input placeholder="Nom" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <Button onClick={invite} className="bg-[#E5276A] hover:bg-[#C01661]">
          <UserPlus className="w-4 h-4 mr-1" /> Inviter
        </Button>
      </CardContent></Card>
      <div className="grid gap-2">
        {staff.map(s => (
          <Card key={s.id}><CardContent className="p-3 flex items-center justify-between text-sm">
            <div>
              <div className="font-semibold">{s.name || s.email}</div>
              <div className="text-xs text-neutral-500">{s.email}</div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-lg font-mono font-bold text-[#E5276A]">{s.pin}</span>
              <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(s.pin); toast.success("Copié"); }}>
                <Copy className="w-3 h-3" />
              </Button>
            </div>
          </CardContent></Card>
        ))}
      </div>
    </div>
  );
}

function OrdersTab({ eventId }) {
  const [orders, setOrders] = useState([]);
  useEffect(() => { proXpApi.listOrders(eventId).then(setOrders); }, [eventId]);
  return (
    <div className="grid gap-2">
      {orders.length === 0 && <p className="text-sm text-neutral-500 text-center py-6">Aucune commande.</p>}
      {orders.map(o => (
        <Card key={o.id}><CardContent className="p-3 text-sm">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-semibold">
                {o.customer_name}
                {o.is_demo && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">DEMO</span>}
              </div>
              <div className="text-xs text-neutral-500">{o.customer_email}</div>
            </div>
            <span className={`text-xs px-2 py-0.5 rounded-full ${o.status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-neutral-100"}`}>{o.status}</span>
          </div>
          <div className="mt-2 grid grid-cols-4 gap-1 text-xs">
            <div>Brut: <b>{formatCents(o.amount_gross_cents, o.currency)}</b></div>
            <div className="text-neutral-500">Stripe: {formatCents(o.stripe_fee_cents, o.currency)}</div>
            <div className="text-neutral-500">Commission: {formatCents(o.application_fee_cents, o.currency)}</div>
            <div className="font-bold text-[#E5276A]">Net: {formatCents(o.amount_net_cents, o.currency)}</div>
          </div>
        </CardContent></Card>
      ))}
    </div>
  );
}


/* ────────── Palmarès tab (Iter65) ────────── */
function PalmaresTab({ eventId }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => proXpApi.getLeaderboard(eventId).then(setData);
  useEffect(() => {
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
    /* eslint-disable-next-line */
  }, [eventId]);
  const startGame = async () => {
    setBusy(true);
    try { await proXpApi.startGame(eventId); toast.success("🎮 Jeu démarré !"); load(); }
    catch { toast.error("Impossible"); }
    finally { setBusy(false); }
  };
  const stopGame = async () => {
    if (!window.confirm("Clôturer le jeu et geler le palmarès ?")) return;
    setBusy(true);
    try { await proXpApi.stopGame(eventId); toast.success("Jeu clôturé"); load(); }
    catch { toast.error("Impossible"); }
    finally { setBusy(false); }
  };
  if (!data) return <p className="text-sm text-neutral-500 text-center py-6">Chargement…</p>;

  return (
    <div>
      <Card className="mb-4 border-2 border-[#E5276A]/30 bg-gradient-to-br from-[#E5276A]/5 to-white">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-1">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#E5276A] font-semibold flex items-center gap-1">
                <Trophy className="w-3.5 h-3.5" /> Mode Jeu
              </div>
              <div className="text-sm text-neutral-600 mt-1">
                {data.game_active
                  ? <>Actif depuis {data.game_started_at ? new Date(data.game_started_at).toLocaleTimeString("fr-FR") : "—"}</>
                  : data.game_ended_at
                    ? <>Clôturé à {new Date(data.game_ended_at).toLocaleTimeString("fr-FR")}</>
                    : "Aucun jeu en cours"}
              </div>
            </div>
            {data.game_active ? (
              <Button onClick={stopGame} disabled={busy} data-testid="stop-game"
                      variant="outline" className="border-red-500 text-red-500">
                <Square className="w-4 h-4 mr-1" /> Clôturer
              </Button>
            ) : (
              <Button onClick={startGame} disabled={busy} data-testid="start-game"
                      className="bg-[#E5276A] hover:bg-[#C01661]">
                <Play className="w-4 h-4 mr-1" /> Lancer le jeu
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {(data.best_individual || data.best_team) && (
        <div className="grid grid-cols-2 gap-3 mb-4">
          {data.best_individual && (
            <Card className="border-2 border-yellow-400 bg-gradient-to-br from-yellow-50 to-white">
              <CardContent className="p-3 text-center">
                <Crown className="w-6 h-6 mx-auto mb-1 text-yellow-500 fill-yellow-300" />
                <div className="text-[10px] uppercase tracking-widest text-yellow-700 font-semibold">Meilleur joueur</div>
                <div className="mt-1 font-bold text-[#1F3A5F] truncate" title={data.best_individual.display_name}>
                  {data.best_individual.display_name}
                </div>
                <div className="text-lg font-bold text-[#E5276A] mt-1">{data.best_individual.points} pts</div>
              </CardContent>
            </Card>
          )}
          {data.best_team && (
            <Card className="border-2 border-blue-400 bg-gradient-to-br from-blue-50 to-white">
              <CardContent className="p-3 text-center">
                <Medal className="w-6 h-6 mx-auto mb-1 text-blue-500" />
                <div className="text-[10px] uppercase tracking-widest text-blue-700 font-semibold">Meilleure équipe</div>
                <div className="mt-1 font-bold text-[#1F3A5F] truncate" title={data.best_team.name}>
                  {data.best_team.name}
                </div>
                <div className="text-lg font-bold text-[#E5276A] mt-1">
                  {data.best_team.points} pts <span className="text-xs text-neutral-500">· {data.best_team.member_count} joueurs</span>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-3">
        <Card>
          <CardContent className="p-3">
            <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2 flex items-center gap-1">
              <Users className="w-3.5 h-3.5" /> Top joueurs
            </div>
            {data.top_individual.length === 0 && <p className="text-xs text-neutral-500 text-center py-3">Aucun joueur avec des points</p>}
            {data.top_individual.map((p, i) => (
              <div key={p.ticket_id} className={`flex items-center gap-2 py-1.5 text-sm border-b last:border-b-0 ${i === 0 ? "font-semibold" : ""}`}>
                <span className={`w-6 h-6 rounded-full text-xs flex items-center justify-center font-bold ${
                  i === 0 ? "bg-yellow-400 text-white" : i === 1 ? "bg-neutral-300 text-white" : i === 2 ? "bg-amber-700 text-white" : "bg-neutral-100 text-neutral-500"
                }`}>{i + 1}</span>
                <span className="flex-1 truncate">{p.display_name}</span>
                <span className="text-[#E5276A] font-mono">{p.points}</span>
              </div>
            ))}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2 flex items-center gap-1">
              <Users className="w-3.5 h-3.5" /> Top équipes
            </div>
            {data.top_teams.length === 0 && <p className="text-xs text-neutral-500 text-center py-3">Aucune équipe active</p>}
            {data.top_teams.map((t, i) => (
              <div key={t.team_id} className={`flex items-center gap-2 py-1.5 text-sm border-b last:border-b-0 ${i === 0 ? "font-semibold" : ""}`}>
                <span className={`w-6 h-6 rounded-full text-xs flex items-center justify-center font-bold ${
                  i === 0 ? "bg-yellow-400 text-white" : i === 1 ? "bg-neutral-300 text-white" : i === 2 ? "bg-amber-700 text-white" : "bg-neutral-100 text-neutral-500"
                }`}>{i + 1}</span>
                <span className="flex-1 truncate">{t.name}</span>
                <span className="text-neutral-500 text-xs">{t.member_count}👥</span>
                <span className="text-[#E5276A] font-mono">{t.points}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}


function KpisTab({ eventId }) {
  const [k, setK] = useState(null);
  useEffect(() => { proXpApi.eventKPIs(eventId).then(setK); }, [eventId]);
  if (!k) return <p className="text-sm text-neutral-500">Chargement…</p>;
  const rb = k.revenue_breakdown;
  return (
    <div>
      <Card className="mb-4"><CardContent className="p-4">
        <div className="text-xs uppercase text-neutral-500 mb-2">Chiffre d'affaires (transparent)</div>
        <div className="grid grid-cols-2 gap-1 text-sm">
          <span>Brut ({k.orders_count} commandes)</span><span className="text-right font-bold">{formatCents(rb.amount_gross_cents)}</span>
          <span className="text-neutral-500">Frais Stripe</span><span className="text-right text-neutral-500">-{formatCents(rb.stripe_fee_cents)}</span>
          <span className="text-neutral-500">Commission geoVINUM</span><span className="text-right text-neutral-500">-{formatCents(rb.application_fee_cents)}</span>
          <span className="font-semibold border-t pt-1 mt-1">Net pour vous</span><span className="text-right font-bold border-t pt-1 mt-1 text-[#E5276A]">{formatCents(rb.amount_net_cents)}</span>
        </div>
      </CardContent></Card>
      <div className="grid grid-cols-3 gap-2 mb-4">
        <StatCard label="Billets actifs" value={k.tickets_active} />
        <StatCard label="Vins au catalogue" value={k.wines_count} />
        <StatCard label="Doses servies" value={k.tastings_served} />
      </div>
      <Card><CardContent className="p-3">
        <div className="text-xs uppercase text-neutral-500 mb-2">Top vins servis</div>
        {k.top_wines_by_service.map(w => (
          <div key={w.wine_event_id} className="flex justify-between py-1 text-sm border-b last:border-b-0">
            <span className="font-mono text-xs">{w.wine_event_id.slice(0, 8)}…</span>
            <b>{w.doses_served} doses</b>
          </div>
        ))}
      </CardContent></Card>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <Card><CardContent className="p-3 text-center">
      <div className="text-2xl font-bold text-[#E5276A]">{value}</div>
      <div className="text-xs text-neutral-500 mt-1">{label}</div>
    </CardContent></Card>
  );
}

function SharePanel({ event }) {
  const publicUrl = `${window.location.origin}/experience/event/${event.slug}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=8&data=${encodeURIComponent(publicUrl)}`;
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: event.name, url: publicUrl }); } catch {}
    } else {
      navigator.clipboard.writeText(publicUrl); toast.success("Lien copié");
    }
  };
  return (
    <Card className="my-4 border-2 border-[#E5276A]/20 bg-gradient-to-br from-[#E5276A]/5 to-white">
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <img src={qrUrl} alt="QR public" className="w-32 h-32 rounded-lg bg-white p-1 border" data-testid="event-qr" />
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-widest text-[#E5276A] font-semibold mb-1 flex items-center gap-1">
              <Share2 className="w-3.5 h-3.5" /> Partager cet événement
            </div>
            <div className="text-sm font-semibold mb-2">Lien public de vente + participation</div>
            <div className="flex items-center gap-1 mb-2">
              <input readOnly value={publicUrl} data-testid="event-public-url"
                     className="flex-1 min-w-0 text-xs font-mono border rounded px-2 py-1.5 bg-white" />
              <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success("Copié"); }}>
                <Copy className="w-3.5 h-3.5" />
              </Button>
              <a href={publicUrl} target="_blank" rel="noreferrer">
                <Button size="sm" variant="outline"><ExternalLink className="w-3.5 h-3.5" /></Button>
              </a>
            </div>
            <Button size="sm" onClick={share} data-testid="event-share-btn"
                    className="bg-[#E5276A] hover:bg-[#C01661]">
              <Share2 className="w-3.5 h-3.5 mr-1" /> Partager
            </Button>
            <p className="text-[11px] text-neutral-500 mt-2">
              Ce QR ouvre la page d'inscription et de paiement. Imprimez-le sur vos flyers ou publiez-le sur les réseaux.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
